import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ArrowDownLeft, ArrowUpRight, Info, AlertTriangle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "../lib/api";
import { formatDateTime } from "../lib/format";

const typeIcon = {
  money_in: { Icon: ArrowDownLeft, cls: "bg-emerald-50 text-emerald-600" },
  money_out: { Icon: ArrowUpRight, cls: "bg-blue-50 text-primary" },
  warning: { Icon: AlertTriangle, cls: "bg-red-50 text-red-600" },
  info: { Icon: Info, cls: "bg-secondary text-muted-foreground" },
};

export const NotificationsBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setItems(res.data.items);
      setUnread(res.data.unread_count);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const onOpenChange = async (v) => {
    setOpen(v);
    if (v && unread > 0) {
      try {
        await api.post("/notifications/read", {});
        setUnread(0);
      } catch {
        // silent
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="relative h-9 w-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-[color,background-color] duration-200"
          aria-label="Notifikasi"
          data-testid="notifications-bell-button"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-r from-[#2E7CF6] to-[#1FC8A9] text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notifications-unread-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 rounded-2xl" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b">
          <p className="font-heading font-semibold text-sm">Notifikasi</p>
        </div>
        <ScrollArea className="max-h-96">
          {items.length === 0 ? (
            <div className="py-10 text-center" data-testid="notifications-empty">
              <Bell className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Belum ada notifikasi</p>
            </div>
          ) : (
            <div className="divide-y divide-border/60">
              {items.map((n) => {
                const { Icon, cls } = typeIcon[n.type] || typeIcon.info;
                return (
                  <button
                    key={n.id}
                    data-testid="notification-item"
                    onClick={() => {
                      setOpen(false);
                      if (n.transaction_id) navigate(`/transaksi/${n.transaction_id}`);
                    }}
                    className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-secondary/60 transition-[background-color] duration-200 ${!n.read ? "bg-primary/[0.03]" : ""}`}
                  >
                    <span className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center ${cls}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{n.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                      <p className="text-[10px] text-muted-foreground/70 mt-0.5">{formatDateTime(n.created_at)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
